import { useEffect, useRef, useState } from 'react';
import { useAuth, useFeature } from '../context/AuthContext';
import { apiGet } from '../lib/api';
import type { ReadHistoryResponse, SeenBy, TicketMessage } from '../types';

/**
 * Historique de lecture (docs/plans/lecture-messages.md) : agents Digital Team
 * administrateurs seulement (is_admin, comme l'API qui refuse en 403 tout
 * autre compte), interrupteur read_tracking. « Vu » = fiche du point ouverte
 * après le message, dans le V1 ou la V2 : pas une lecture garantie.
 */
export function useCanSeeReads(): boolean {
  const { user } = useAuth();
  const open = useFeature('read_tracking');
  return open && user?.is_admin === true;
}

const fmt = (iso: string): string => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
const COTE: Record<SeenBy['cote'], string> = { client: 'client', dt: 'Digital Team', inconnu: 'compte inconnu' };

/** Pastille « Vu par … » sous un message (vu_par n'est renvoyé qu'aux agents DT administrateurs). */
export function ReadBadge({ message }: { message: TicketMessage }) {
  if (!message.vu_par) return null;
  const readers = message.vu_par;
  const title =
    readers.length === 0
      ? 'Personne n’a ouvert le point depuis ce message (V1 et V2). La lecture des courriels n’est pas mesurée.'
      : readers.map((r) => `${r.nom} (${COTE[r.cote]}) : ouvert le ${fmt(r.first_seen)}`).join('\n') +
        '\n\nOuverture de la fiche après le message, pas une lecture garantie.';
  const names = readers.map((r) => r.nom);
  const shown = names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ');
  return (
    <div className={`read-badge ${readers.length === 0 ? 'read-badge-none' : ''}`} title={title}>
      {readers.length === 0 ? 'Non vu' : `Vu par ${shown}`}
    </div>
  );
}

/** Lien « Historique de lecture » et sa fenêtre (dialog natif, showModal). */
export function ReadHistoryLink({ displayId }: { displayId: number }) {
  const canSee = useCanSeeReads();
  const [open, setOpen] = useState(false);
  if (!canSee) return null;
  return (
    <>
      <button type="button" className="link-button" onClick={() => setOpen(true)}>
        Historique de lecture
      </button>
      {open && <ReadHistoryDialog displayId={displayId} onClose={() => setOpen(false)} />}
    </>
  );
}

function ReadHistoryDialog({ displayId, onClose }: { displayId: number; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [data, setData] = useState<ReadHistoryResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [clientsOnly, setClientsOnly] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    dialog.showModal();
    return () => dialog.close();
  }, []);

  useEffect(() => {
    const ctl = new AbortController();
    apiGet<ReadHistoryResponse>(`/api/tickets/${displayId}/reads`, ctl.signal)
      .then((r) => {
        if (!ctl.signal.aborted) setData(r);
      })
      .catch((e) => {
        if (!ctl.signal.aborted) setErr(e instanceof Error ? e.message : 'Erreur');
      });
    return () => ctl.abort();
  }, [displayId]);

  const items = (data?.items ?? []).filter((i) => !clientsOnly || i.cote === 'client');

  return (
    <dialog
      ref={ref}
      className="read-dialog"
      aria-labelledby="read-dialog-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // Clic sur le fond (hors du contenu) : fermeture.
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="read-dialog-body">
        <div className="read-dialog-head">
          <h2 id="read-dialog-title">Historique de lecture</h2>
          <button type="button" className="read-dialog-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>
        <p className="read-dialog-note">
          Ouvertures de la fiche du point dans Jarvis V1 et V2, {data?.retention_months ?? 12} derniers mois. Ouverture de la
          fiche, pas lecture garantie ; la lecture des courriels de notification n’est pas mesurée. Les accès par clé d’API ne
          sont pas comptés.
        </p>
        <label className="read-dialog-filter">
          <input type="checkbox" checked={clientsOnly} onChange={(e) => setClientsOnly(e.target.checked)} /> Clients seulement
        </label>
        {err && <div style={{ color: 'var(--danger)' }}>{err}</div>}
        {!data && !err && <div style={{ color: 'var(--text-mute)' }}>Chargement…</div>}
        {data && !data.v1_available && <div className="read-dialog-warn">Ouvertures du V1 indisponibles pour le moment.</div>}
        {data && data.truncated && <div className="read-dialog-warn">Liste tronquée aux ouvertures les plus récentes.</div>}
        {data && items.length === 0 && <div style={{ color: 'var(--text-mute)' }}>Aucune ouverture.</div>}
        {data && items.length > 0 && (
          <div className="table-scroll">
            <table className="read-table">
              <thead>
                <tr>
                  <th>Date et heure</th>
                  <th>Nom</th>
                  <th>Login</th>
                  <th>IP</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i, k) => (
                  <tr key={`${i.source}-${i.at}-${k}`}>
                    <td>{fmt(i.at)}</td>
                    <td>
                      {i.nom ?? '—'}
                      {i.cote === 'dt' && <span className="read-cote"> DT</span>}
                    </td>
                    <td>{i.login}</td>
                    <td>{i.ip ?? 'non disponible'}</td>
                    <td>{i.source === 'v1' ? 'V1' : 'V2'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </dialog>
  );
}
